import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Trophy,
  Plus,
  Search,
  Calendar,
  MapPin,
  Users,
  Edit2,
  Trash2,
  Eye,
  Star,
  Clock,
  X,
  FileSpreadsheet
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { getTournaments, deleteTournament, toggleFeatured } from '../services/tournamentService';
import { getAdminData } from '../services/adminAuth';
import CustomSelect from '../components/common/CustomSelect';
import AdminTournamentDraw from './AdminTournamentDraw';

const AdminTournaments = () => {
  const { isDarkMode } = useTheme();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const adminData = getAdminData();

  // Draw modal state
  const [showDrawModal, setShowDrawModal] = useState(false);
  const [selectedTournament, setSelectedTournament] = useState(null);

  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'Mens_Open', label: "Men's Open" },
    { value: 'Mens_Mid_Am', label: "Men's Mid-Am" },
    { value: 'Mens_Senior', label: "Men's Senior" },
    { value: 'Mens_Super_Senior', label: "Men's Super Senior" },
    { value: 'Ladies_Open', label: "Ladies Open" },
    { value: 'Ladies_Mid_Am', label: "Ladies Mid-Am" },
    { value: 'Ladies_Senior', label: "Ladies Senior" },
    { value: 'Ladies_Super_Senior', label: "Ladies Super Senior" },
    { value: 'Junior_Boys', label: "Junior Boys" },
    { value: 'Junior_Girls', label: "Junior Girls" },
    { value: 'Mixed', label: "Mixed" },
    { value: 'Country_Districts', label: "Country Districts" }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'draft', label: 'Draft' },
    { value: 'published', label: 'Published' },
    { value: 'registration_open', label: 'Registration Open' },
    { value: 'registration_closed', label: 'Registration Closed' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  useEffect(() => {
    fetchTournaments();
  }, [filterCategory, filterStatus]);

  const fetchTournaments = async () => {
    try {
      setLoading(true);
      const filters = {};
      
      if (filterCategory !== 'all') filters.category = filterCategory;
      if (filterStatus !== 'all') filters.status = filterStatus;
      
      const response = await getTournaments(filters);
      setTournaments(response.data || []);
    } catch (error) {
      console.error('Error fetching tournaments:', error);
      alert('Failed to load tournaments');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (tournament) => {
    if (!window.confirm(`Are you sure you want to delete "${tournament.tournament_name}"?`)) {
      return;
    }
    
    try {
      await deleteTournament(tournament.id, adminData.id);
      alert('Tournament deleted successfully');
      fetchTournaments();
    } catch (error) {
      console.error('Error deleting tournament:', error);
      alert(error.message || 'Failed to delete tournament');
    }
  };

  const handleToggleFeatured = async (tournament) => {
    try {
      await toggleFeatured(tournament.id, !tournament.is_featured, adminData.id);
      fetchTournaments();
    } catch (error) {
      console.error('Error toggling featured:', error);
      alert('Failed to update featured status');
    }
  };

  const openDrawModal = (tournament = null) => {
    setSelectedTournament(tournament);
    setShowDrawModal(true);
  };

  const filteredTournaments = tournaments.filter(tournament => {
    const matchesSearch = 
      tournament.tournament_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tournament.venue_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (tournament.venue_city && tournament.venue_city.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesSearch;
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'published':
      case 'registration_open':
        return isDarkMode
          ? 'bg-fsgu-green-900/30 text-fsgu-green-400'
          : 'bg-fsgu-green-100 text-fsgu-green-700';
      case 'draft':
        return isDarkMode
          ? 'bg-fsgu-gold-900/30 text-fsgu-gold-400'
          : 'bg-fsgu-gold-100 text-fsgu-gold-700';
      case 'registration_closed':
      case 'in_progress':
        return isDarkMode
          ? 'bg-fsgu-blue-900/30 text-fsgu-blue-400'
          : 'bg-fsgu-blue-100 text-fsgu-blue-700';
      case 'completed':
        return isDarkMode
          ? 'bg-fsgu-gray-700 text-fsgu-silver-400'
          : 'bg-fsgu-silver-200 text-fsgu-silver-700';
      case 'cancelled':
        return isDarkMode
          ? 'bg-red-900/30 text-red-400'
          : 'bg-red-100 text-red-700';
      default:
        return isDarkMode
          ? 'bg-fsgu-gray-700 text-fsgu-silver-300'
          : 'bg-fsgu-silver-200 text-fsgu-silver-700';
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Mens_Open': isDarkMode ? 'bg-fsgu-blue-900/30 text-fsgu-blue-400' : 'bg-fsgu-blue-100 text-fsgu-blue-700',
      'Mens_Mid_Am': isDarkMode ? 'bg-cyan-900/30 text-cyan-400' : 'bg-cyan-100 text-cyan-700',
      'Mens_Senior': isDarkMode ? 'bg-purple-900/30 text-purple-400' : 'bg-purple-100 text-purple-700',
      'Mens_Super_Senior': isDarkMode ? 'bg-violet-900/30 text-violet-400' : 'bg-violet-100 text-violet-700',
      'Ladies_Open': isDarkMode ? 'bg-fsgu-gold-900/30 text-fsgu-gold-400' : 'bg-fsgu-gold-100 text-fsgu-gold-700',
      'Ladies_Mid_Am': isDarkMode ? 'bg-amber-900/30 text-amber-400' : 'bg-amber-100 text-amber-700',
      'Ladies_Senior': isDarkMode ? 'bg-pink-900/30 text-pink-400' : 'bg-pink-100 text-pink-700',
      'Ladies_Super_Senior': isDarkMode ? 'bg-fuchsia-900/30 text-fuchsia-400' : 'bg-fuchsia-100 text-fuchsia-700',
      'Junior_Boys': isDarkMode ? 'bg-fsgu-green-900/30 text-fsgu-green-400' : 'bg-fsgu-green-100 text-fsgu-green-700',
      'Junior_Girls': isDarkMode ? 'bg-rose-900/30 text-rose-400' : 'bg-rose-100 text-rose-700',
      'Mixed': isDarkMode ? 'bg-indigo-900/30 text-indigo-400' : 'bg-indigo-100 text-indigo-700',
      'Country_Districts': isDarkMode ? 'bg-teal-900/30 text-teal-400' : 'bg-teal-100 text-teal-700',
    };
    return colors[category] || (isDarkMode ? 'bg-fsgu-gray-700 text-fsgu-silver-300' : 'bg-fsgu-silver-200 text-fsgu-silver-700');
  };

  const formatCategoryLabel = (category) => {
    const labels = {
      'Mens_Open': "Men's Open",
      'Mens_Mid_Am': "Men's Mid-Am",
      'Mens_Senior': "Men's Senior",
      'Mens_Super_Senior': "Men's Super Senior",
      'Ladies_Open': "Ladies Open",
      'Ladies_Mid_Am': "Ladies Mid-Am",
      'Ladies_Senior': "Ladies Senior",
      'Ladies_Super_Senior': "Ladies Super Senior",
      'Junior_Boys': "Junior Boys",
      'Junior_Girls': "Junior Girls",
      'Mixed': 'Mixed',
      'Country_Districts': 'Country Districts'
    };
    return labels[category] || category.replace(/_/g, ' ');
  };

  return (
    <div className={`min-h-screen ${
      isDarkMode ? 'bg-fsgu-gray-900' : 'bg-fsgu-silver-50'
    }`}>
      <div className="container-custom py-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className={`text-3xl sm:text-4xl font-bold font-cursive ${
              isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
            }`}>
              Tournaments
            </h1>
            <p className={`text-sm mt-1 ${
              isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
            }`}>
              Manage tournaments and player draws
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={() => openDrawModal()}
              className={`inline-flex items-center justify-center px-4 py-2.5 rounded-lg font-medium transition-colors ${
                isDarkMode
                  ? 'bg-fsgu-blue-500/20 text-fsgu-blue-400 hover:bg-fsgu-blue-500/30'
                  : 'bg-fsgu-blue-500/10 text-fsgu-blue-600 hover:bg-fsgu-blue-500/20'
              }`}
            >
              <FileSpreadsheet className="w-5 h-5 mr-2" />
              Manage Draw
            </button>
            
            <Link
              to="/admin/tournaments/add"
              className="btn btn-primary inline-flex items-center justify-center"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Tournament
            </Link>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className={`p-6 rounded-xl border ${
            isDarkMode
              ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
              : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
          }`}>
            <div className="flex items-center justify-between mb-2">
              <span className={`text-sm font-medium ${
                isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
              }`}>
                Total Tournaments
              </span>
              <div className={`p-2 rounded-lg ${
                isDarkMode ? 'bg-fsgu-green-500/20' : 'bg-fsgu-green-500/10'
              }`}>
                <Trophy className={`w-5 h-5 ${
                  isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
                }`} />
              </div>
            </div>
            <div className={`text-3xl font-bold ${
              isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
            }`}>
              {tournaments.length}
            </div>
          </div>

          <div className={`p-6 rounded-xl border ${
            isDarkMode
              ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
              : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
          }`}>
            <div className="flex items-center justify-between mb-2">
              <span className={`text-sm font-medium ${
                isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
              }`}>
                Upcoming
              </span>
              <div className={`p-2 rounded-lg ${
                isDarkMode ? 'bg-fsgu-blue-500/20' : 'bg-fsgu-blue-500/10'
              }`}>
                <Calendar className={`w-5 h-5 ${
                  isDarkMode ? 'text-fsgu-blue-400' : 'text-fsgu-blue-500'
                }`} />
              </div>
            </div>
            <div className={`text-3xl font-bold ${
              isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
            }`}>
              {tournaments.filter(t => t.is_upcoming).length}
            </div>
          </div>

          <div className={`p-6 rounded-xl border ${
            isDarkMode
              ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
              : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
          }`}>
            <div className="flex items-center justify-between mb-2">
              <span className={`text-sm font-medium ${
                isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
              }`}>
                Published
              </span>
              <div className={`p-2 rounded-lg ${
                isDarkMode ? 'bg-fsgu-gold-500/20' : 'bg-fsgu-gold-500/10'
              }`}>
                <Users className={`w-5 h-5 ${
                  isDarkMode ? 'text-fsgu-gold-400' : 'text-fsgu-gold-500'
                }`} />
              </div>
            </div>
            <div className={`text-3xl font-bold ${
              isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
            }`}>
              {tournaments.filter(t => t.status === 'published').length}
            </div>
          </div>

          <div className={`p-6 rounded-xl border ${
            isDarkMode
              ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
              : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
          }`}>
            <div className="flex items-center justify-between mb-2">
              <span className={`text-sm font-medium ${
                isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
              }`}>
                Drafts
              </span>
              <div className={`p-2 rounded-lg ${
                isDarkMode ? 'bg-fsgu-silver-500/20' : 'bg-fsgu-silver-500/10'
              }`}>
                <Clock className={`w-5 h-5 ${
                  isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-500'
                }`} />
              </div>
            </div>
            <div className={`text-3xl font-bold ${
              isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
            }`}>
              {tournaments.filter(t => t.status === 'draft').length}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className={`p-6 rounded-xl border ${
          isDarkMode
            ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
            : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
        }`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative sm:col-span-2 lg:col-span-1">
              <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 pointer-events-none ${
                isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-400'
              }`} />
              <input
                type="text"
                placeholder="Search tournaments..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input pl-10 w-full"
              />
            </div>

            {/* Category Filter */}
            <CustomSelect
              name="filterCategory"
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              options={categories}
              placeholder="Select Category"
            />

            {/* Status Filter */}
            <CustomSelect
              name="filterStatus"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              options={statusOptions}
              placeholder="Select Status"
            />
          </div>
        </div>

        {/* Tournaments Table/Cards */}
        <div className={`rounded-xl border overflow-hidden ${
          isDarkMode
            ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
            : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
        }`}>
          {loading ? (
            <div className="p-12 text-center">
              <div className={`inline-block animate-spin rounded-full h-12 w-12 border-4 border-solid border-current border-r-transparent mb-4 ${
                isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
              }`}></div>
              <p className={isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'}>
                Loading tournaments...
              </p>
            </div>
          ) : filteredTournaments.length === 0 ? (
            <div className="p-12 text-center">
              <Trophy className={`w-16 h-16 mx-auto mb-4 ${
                isDarkMode ? 'text-fsgu-silver-600' : 'text-fsgu-silver-400'
              }`} />
              <p className={`text-lg font-medium mb-1 ${
                isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
              }`}>
                No tournaments found
              </p>
              <p className={`text-sm ${
                isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
              }`}>
                Try adjusting your filters or create a new tournament
              </p>
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full">
                  <thead className={`border-b ${
                    isDarkMode ? 'border-fsgu-gray-700 bg-fsgu-gray-900/50' : 'border-fsgu-silver-200 bg-fsgu-silver-50'
                  }`}>
                    <tr>
                      <th className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider ${
                        isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                      }`}>
                        Tournament
                      </th>
                      <th className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider ${
                        isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                      }`}>
                        Category
                      </th>
                      <th className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider ${
                        isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                      }`}>
                        Dates
                      </th>
                      <th className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider ${
                        isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                      }`}>
                        Venue
                      </th>
                      <th className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider ${
                        isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                      }`}>
                        Status
                      </th>
                      <th className={`px-6 py-4 text-right text-xs font-semibold uppercase tracking-wider ${
                        isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                      }`}>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${
                    isDarkMode ? 'divide-fsgu-gray-700' : 'divide-fsgu-silver-200'
                  }`}>
                    {filteredTournaments.map((tournament) => (
                      <tr
                        key={tournament.id}
                        className={`transition-colors duration-150 ${
                          isDarkMode
                            ? 'hover:bg-fsgu-gray-700/50'
                            : 'hover:bg-fsgu-silver-50'
                        }`}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-start space-x-3">
                            {tournament.is_featured && (
                              <Star className="w-4 h-4 text-fsgu-gold-500 flex-shrink-0 mt-1" fill="currentColor" />
                            )}
                            <div>
                              <div className={`font-semibold ${
                                isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
                              }`}>
                                {tournament.tournament_name}
                              </div>
                              {tournament.tournament_short_name && (
                                <div className={`text-xs mt-0.5 ${
                                  isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
                                }`}>
                                  {tournament.tournament_short_name}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                            getCategoryColor(tournament.category)
                          }`}>
                            {formatCategoryLabel(tournament.category)}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className={`text-sm ${
                            isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                          }`}>
                            <div>{formatDate(tournament.start_date)}</div>
                            {tournament.start_date !== tournament.end_date && (
                              <div className={`text-xs ${
                                isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
                              }`}>
                                to {formatDate(tournament.end_date)}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className={`flex items-start text-sm ${
                            isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                          }`}>
                            <MapPin className={`w-4 h-4 mr-2 mt-0.5 flex-shrink-0 ${
                              isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-400'
                            }`} />
                            <div>
                              <div>{tournament.venue_name}</div>
                              {tournament.venue_city && (
                                <div className={`text-xs ${
                                  isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
                                }`}>
                                  {tournament.venue_city}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                            getStatusColor(tournament.status)
                          }`}>
                            {tournament.status.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end space-x-1">
                            <button
                              onClick={() => handleToggleFeatured(tournament)}
                              className={`p-2 rounded-lg transition-colors ${
                                tournament.is_featured
                                  ? isDarkMode
                                    ? 'text-fsgu-gold-400 hover:bg-fsgu-gold-900/30'
                                    : 'text-fsgu-gold-500 hover:bg-fsgu-gold-50'
                                  : isDarkMode
                                  ? 'text-fsgu-silver-400 hover:bg-fsgu-gray-700'
                                  : 'text-fsgu-silver-500 hover:bg-fsgu-silver-100'
                              }`}
                              title={tournament.is_featured ? 'Remove featured' : 'Add to featured'}
                            >
                              <Star className="w-4 h-4" fill={tournament.is_featured ? 'currentColor' : 'none'} />
                            </button>
                            
                            <button
                              onClick={() => openDrawModal(tournament)}
                              className={`p-2 rounded-lg transition-colors ${
                                isDarkMode
                                  ? 'text-fsgu-blue-400 hover:bg-fsgu-blue-900/30'
                                  : 'text-fsgu-blue-600 hover:bg-fsgu-blue-50'
                              }`}
                              title="Manage draw"
                            >
                              <FileSpreadsheet className="w-4 h-4" />
                            </button>
                            
                            <Link
                              to={`/admin/tournaments/${tournament.id}`}
                              className={`p-2 rounded-lg transition-colors ${
                                isDarkMode
                                  ? 'text-fsgu-green-400 hover:bg-fsgu-green-900/30'
                                  : 'text-fsgu-green-600 hover:bg-fsgu-green-50'
                              }`}
                              title="View details"
                            >
                              <Eye className="w-4 h-4" />
                            </Link>
                            
                            <Link
                              to={`/admin/tournaments/edit/${tournament.id}`}
                              className={`p-2 rounded-lg transition-colors ${
                                isDarkMode
                                  ? 'text-purple-400 hover:bg-purple-900/30'
                                  : 'text-purple-600 hover:bg-purple-50'
                              }`}
                              title="Edit"
                            >
                              <Edit2 className="w-4 h-4" />
                            </Link>
                            
                            <button
                              onClick={() => handleDelete(tournament)}
                              className={`p-2 rounded-lg transition-colors ${
                                isDarkMode
                                  ? 'text-red-400 hover:bg-red-900/30'
                                  : 'text-red-600 hover:bg-red-50'
                              }`}
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className={`lg:hidden divide-y ${
                isDarkMode ? 'divide-fsgu-gray-700' : 'divide-fsgu-silver-200'
              }`}>
                {filteredTournaments.map((tournament) => (
                  <div
                    key={tournament.id}
                    className={`p-6 transition-colors ${
                      isDarkMode
                        ? 'hover:bg-fsgu-gray-700/50'
                        : 'hover:bg-fsgu-silver-50'
                    }`}
                  >
                    {/* Tournament Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          {tournament.is_featured && (
                            <Star className="w-4 h-4 text-fsgu-gold-500 flex-shrink-0" fill="currentColor" />
                          )}
                          <h3 className={`font-semibold ${
                            isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
                          }`}>
                            {tournament.tournament_name}
                          </h3>
                        </div>
                      </div>
                      
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ml-2 ${
                        getStatusColor(tournament.status)
                      }`}>
                        {tournament.status.replace('_', ' ')}
                      </span>
                    </div>

                    {/* Tournament Details */}
                    <div className="space-y-3 mb-4">
                      <div className="flex items-center">
                        <Trophy className={`w-4 h-4 mr-2 ${
                          isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-400'
                        }`} />
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                          getCategoryColor(tournament.category)
                        }`}>
                          {formatCategoryLabel(tournament.category)}
                        </span>
                      </div>

                      <div className={`flex items-center text-sm ${
                        isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                      }`}>
                        <Calendar className={`w-4 h-4 mr-2 flex-shrink-0 ${
                          isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-400'
                        }`} />
                        <div>
                          {formatDate(tournament.start_date)}
                          {tournament.start_date !== tournament.end_date && (
                            <> - {formatDate(tournament.end_date)}</>
                          )}
                        </div>
                      </div>

                      <div className={`flex items-start text-sm ${
                        isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                      }`}>
                        <MapPin className={`w-4 h-4 mr-2 mt-0.5 flex-shrink-0 ${
                          isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-400'
                        }`} />
                        <div>{tournament.venue_name}</div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className={`flex items-center justify-end space-x-2 pt-4 border-t ${
                      isDarkMode ? 'border-fsgu-gray-700' : 'border-fsgu-silver-200'
                    }`}>
                      <button
                        onClick={() => handleToggleFeatured(tournament)}
                        className={`p-2 rounded-lg transition-colors ${
                          tournament.is_featured
                            ? isDarkMode
                              ? 'text-fsgu-gold-400 hover:bg-fsgu-gold-900/30'
                              : 'text-fsgu-gold-500 hover:bg-fsgu-gold-50'
                            : isDarkMode
                            ? 'text-fsgu-silver-400 hover:bg-fsgu-gray-700'
                            : 'text-fsgu-silver-500 hover:bg-fsgu-silver-100'
                        }`}
                      >
                        <Star className="w-5 h-5" fill={tournament.is_featured ? 'currentColor' : 'none'} />
                      </button>
                      
                      <button
                        onClick={() => openDrawModal(tournament)}
                        className={`p-2 rounded-lg transition-colors ${
                          isDarkMode
                            ? 'text-fsgu-blue-400 hover:bg-fsgu-blue-900/30'
                            : 'text-fsgu-blue-600 hover:bg-fsgu-blue-50'
                        }`}
                      >
                        <FileSpreadsheet className="w-5 h-5" />
                      </button>
                      
                      <Link
                        to={`/admin/tournaments/${tournament.id}`}
                        className={`p-2 rounded-lg transition-colors ${
                          isDarkMode
                            ? 'text-fsgu-green-400 hover:bg-fsgu-green-900/30'
                            : 'text-fsgu-green-600 hover:bg-fsgu-green-50'
                        }`}
                      >
                        <Eye className="w-5 h-5" />
                      </Link>
                      
                      <Link
                        to={`/admin/tournaments/edit/${tournament.id}`}
                        className={`p-2 rounded-lg transition-colors ${
                          isDarkMode
                            ? 'text-purple-400 hover:bg-purple-900/30'
                            : 'text-purple-600 hover:bg-purple-50'
                        }`}
                      >
                        <Edit2 className="w-5 h-5" />
                      </Link>
                      
                      <button
                        onClick={() => handleDelete(tournament)}
                        className={`p-2 rounded-lg transition-colors ${
                          isDarkMode
                            ? 'text-red-400 hover:bg-red-900/30'
                            : 'text-red-600 hover:bg-red-50'
                        }`}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Draw Management Modal */}
      {showDrawModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div
            className={`w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-xl shadow-xl flex flex-col ${
              isDarkMode ? 'bg-fsgu-gray-800' : 'bg-white'
            }`}
          >
            {/* Modal Header */}
            <div
              className={`flex items-center justify-between px-6 py-4 border-b flex-shrink-0 ${
                isDarkMode ? 'border-fsgu-gray-700' : 'border-fsgu-silver-200'
              }`}
            >
              <div>
                <h2
                  className={`text-xl font-bold ${
                    isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
                  }`}
                >
                  Manage Tournament Draw
                </h2>
                {selectedTournament && (
                  <p className={`text-sm mt-1 ${
                    isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                  }`}>
                    {selectedTournament.tournament_name}
                  </p>
                )}
              </div>
              <button
                onClick={() => {
                  setShowDrawModal(false);
                  setSelectedTournament(null);
                }}
                className={`p-2 rounded-lg ${
                  isDarkMode
                    ? 'text-fsgu-silver-400 hover:bg-fsgu-gray-700'
                    : 'text-fsgu-silver-500 hover:bg-fsgu-silver-100'
                }`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6">
              {!selectedTournament ? (
                // Tournament Selection
                <div className="space-y-4">
                  <p className={`text-sm ${
                    isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                  }`}>
                    Select a tournament to manage its draw:
                  </p>
                  <div className="grid gap-3 max-h-96 overflow-y-auto">
                    {tournaments.map((t) => (
                      <button
                        key={t.id}
                        onClick={() => setSelectedTournament(t)}
                        className={`p-4 rounded-lg border text-left transition-colors ${
                          isDarkMode
                            ? 'border-fsgu-gray-700 hover:bg-fsgu-gray-700 hover:border-fsgu-blue-500'
                            : 'border-fsgu-silver-200 hover:bg-fsgu-silver-50 hover:border-fsgu-blue-500'
                        }`}
                      >
                        <div className={`font-medium ${
                          isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
                        }`}>
                          {t.tournament_name}
                        </div>
                        <div className={`text-sm mt-1 flex items-center gap-3 ${
                          isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                        }`}>
                          <span>{formatDate(t.start_date)}</span>
                          <span>•</span>
                          <span>{t.venue_name}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                // Draw Management Component
                <AdminTournamentDraw
                  tournamentId={selectedTournament.id}
                  tournamentName={selectedTournament.tournament_name}
                />
              )}
            </div>

            {/* Modal Footer */}
            {selectedTournament && (
              <div
                className={`flex justify-between items-center px-6 py-4 border-t flex-shrink-0 ${
                  isDarkMode ? 'border-fsgu-gray-700' : 'border-fsgu-silver-200'
                }`}
              >
                <button
                  onClick={() => setSelectedTournament(null)}
                  className={`px-4 py-2 rounded-lg font-medium ${
                    isDarkMode
                      ? 'bg-fsgu-gray-700 text-fsgu-silver-300 hover:bg-fsgu-gray-600'
                      : 'bg-fsgu-silver-100 text-fsgu-silver-700 hover:bg-fsgu-silver-200'
                  }`}
                >
                  ← Back to Tournaments
                </button>
                <button
                  onClick={() => {
                    setShowDrawModal(false);
                    setSelectedTournament(null);
                  }}
                  className="btn btn-primary"
                >
                  Done
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTournaments;